import { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Trophy, Search, Crown, Medal } from "lucide-react";
import { SeedAvatar, BadgeChip } from "@/components/SeedAvatar";
import { getLeaderboardV2, getMyRanks } from "@/lib/afritask";

const fmtNaira = (n) => `₦${Number(n).toLocaleString("en-NG")}`;

const CATEGORIES = [
  { value: "earnings", label: "💰 Top Earners", suffix: (v) => fmtNaira(v), tone: "text-emerald-700" },
  { value: "wins", label: "🏆 Most Completed", suffix: (v) => `${v} completed`, tone: "text-amber-800" },
  { value: "streak", label: "🔥 Longest Streaks", suffix: (v) => `${v} days`, tone: "text-rose-600" },
];

export default function Leaderboard() {
  const { me } = useOutletContext();
  const [category, setCategory] = useState("earnings");
  const [board, setBoard] = useState(null);
  const [ranks, setRanks] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [b, r] = await Promise.all([
        getLeaderboardV2("all_time", category, 50),
        me ? getMyRanks().catch(() => null) : Promise.resolve(null),
      ]);
      setBoard(b);
      setRanks(r);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, me?.user_id]);

  const cat = CATEGORIES.find((c) => c.value === category);
  const entries = board?.entries || [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((e) => (e.display_name || e.username).toLowerCase().includes(q));
  }, [entries, search]);

  const podium = filtered.slice(0, 3);
  const rest = filtered.slice(3);

  const myRank = ranks?.ranks?.all_time?.[category] ?? null;
  const myEntry = entries.find((e) => e.user_id === me?.user_id);
  const myName = me?.display_name || me?.username;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Leaderboard</h1>
        </div>
        <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
          <Trophy className="mr-1 h-3 w-3" /> Live
        </Badge>
      </div>

      {/* Category pills */}
      <div className="-mx-6 overflow-x-auto px-6 sm:mx-0 sm:px-0">
        <div className="flex gap-2 pb-1" data-testid="lb-category-pills">
          {CATEGORIES.map((c) => {
            const active = category === c.value;
            return (
              <button
                key={c.value}
                onClick={() => setCategory(c.value)}
                data-testid={`lb-category-${c.value}`}
                className={`whitespace-nowrap rounded-full border px-4 py-1.5 text-sm font-medium transition ${
                  active
                    ? "border-amber-700 bg-amber-700 text-white shadow-sm"
                    : "border-neutral-200 bg-white text-neutral-700 hover:border-neutral-300 hover:bg-neutral-50"
                }`}
              >
                {c.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Your rank card */}
      {me && (
        <Card
          className={
            myRank ? "border-amber-200 bg-amber-50/40" : "border-dashed border-neutral-300 bg-neutral-50"
          }
          data-testid="your-rank-card"
        >
          <CardContent className="flex items-center justify-between gap-4 p-5">
            <div className="flex items-center gap-4">
              <SeedAvatar seed={myName} size={48} />
              <div>
                <p className="text-xs uppercase tracking-wider text-neutral-500">Your rank</p>
                {myRank ? (
                  <p className="mt-0.5 text-lg font-bold">
                    You&apos;re <span className="text-amber-700">#{myRank}</span> all-time · {cat.label}
                  </p>
                ) : (
                  <p className="mt-0.5 text-sm font-medium text-neutral-700">
                    Not ranked yet — win a round to climb the board.
                  </p>
                )}
                {myEntry && cat && (
                  <p className="mt-0.5 text-xs text-neutral-500">
                    Your value: <span className={`font-semibold ${cat.tone}`}>{cat.suffix(myEntry.value)}</span>
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
        <Input
          placeholder="Search users…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="lb-search-input"
        />
      </div>

      {loading && !board && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-neutral-500">Loading…</CardContent>
        </Card>
      )}

      {/* Podium */}
      {podium.length > 0 && (
        <div className="mx-auto grid max-w-3xl grid-cols-3 gap-3" data-testid="lb-podium">
          {[podium[1], podium[0], podium[2]].filter(Boolean).map((e, idx) => {
            const trueRank = e.rank;
            const bgs = {
              1: "from-amber-100 to-amber-50 border-amber-300",
              2: "from-neutral-200 to-neutral-100 border-neutral-300",
              3: "from-amber-700/20 to-amber-700/5 border-amber-700/30",
            };
            const RankIcon = trueRank === 1 ? Crown : Medal;
            const name = e.display_name || e.username;
            return (
              <Card
                key={e.user_id}
                className={`relative overflow-hidden border-2 bg-gradient-to-br ${bgs[trueRank]}`}
                style={{ order: idx }}
              >
                <CardContent className="flex flex-col items-center justify-end gap-1 p-3">
                  <RankIcon
                    className={`absolute right-2 top-2 h-4 w-4 ${
                      trueRank === 1 ? "text-amber-500" : trueRank === 2 ? "text-neutral-500" : "text-amber-700"
                    }`}
                  />
                  <span className="text-lg font-black tabular-nums text-neutral-800">#{trueRank}</span>
                  <SeedAvatar seed={name} size={40} />
                  <p className="line-clamp-1 max-w-full text-center text-xs font-semibold">{name}</p>
                  <p className={`text-sm font-bold tabular-nums ${cat.tone}`}>{cat.suffix(e.value)}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Ranked list */}
      <Card>
        <CardContent className="p-0">
          {rest.length === 0 && podium.length === 0 && !loading && (
            <div className="py-12 text-center text-sm text-neutral-500">
              No entries for this category yet.
            </div>
          )}
          <ul className="divide-y divide-neutral-100" data-testid="lb-ranked-list">
            {rest.map((e) => {
              const isMe = e.user_id === me?.user_id;
              const name = e.display_name || e.username;
              return (
                <li
                  key={e.user_id}
                  className={`flex items-center gap-4 px-5 py-3 ${isMe ? "bg-amber-50/60" : ""}`}
                  data-testid={`lb-row-${e.rank}`}
                >
                  <span className="w-6 text-sm font-semibold tabular-nums text-neutral-500">#{e.rank}</span>
                  <SeedAvatar seed={name} size={36} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-neutral-900">
                      {name} {isMe && <span className="ml-1 text-xs text-amber-700">(you)</span>}
                    </p>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-neutral-500">
                      <span className="text-rose-500">🔥 {e.subtitle_metrics.streak_days}d</span>
                      <span className="text-emerald-700">{fmtNaira(e.subtitle_metrics.earnings_ngn)}</span>
                    </div>
                  </div>
                  {e.badges?.length > 0 && (
                    <div className="hidden gap-1 sm:flex">
                      {e.badges.slice(0, 3).map((b) => (
                        <BadgeChip key={b.code} badge={b} size="sm" />
                      ))}
                    </div>
                  )}
                  <span className={`w-28 shrink-0 text-right font-semibold tabular-nums ${cat.tone}`}>
                    {cat.suffix(e.value)}
                  </span>
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
